
// Color Palette Generator Agent - Uses runAgent edge function with SambaNova API

// Color Palette Generator Agent - Uses GPT OSS for color generation

export interface ColorPalette {
  name: string;
  colors: string[];
  description: string;
}

// Mock palettes for fallback
const mockColorPalettes: Record<string, ColorPalette> = {
  ocean: {
    name: 'Ocean',
    colors: ['#001f3f', '#0074D9', '#39CCCC', '#7FDBFF', '#B3E5E1'],
    description: 'Cool blues and teals inspired by the ocean'
  },
  sunset: {
    name: 'Sunset',
    colors: ['#FF4136', '#FF851B', '#FFDC00', '#FFB347', '#FFA07A'],
    description: 'Warm oranges and reds of a beautiful sunset'
  },
  forest: {
    name: 'Forest',
    colors: ['#2D5016', '#3D6B2D', '#4F7942', '#6B8E5A', '#87A96B'],
    description: 'Natural greens found in a lush forest'
  },
  lavender: {
    name: 'Lavender',
    colors: ['#663399', '#8A2BE2', '#9370DB', '#BA55D3', '#DDA0DD'],
    description: 'Soft purples and lavender tones'
  },
  monochrome: {
    name: 'Monochrome',
    colors: ['#000000', '#333333', '#666666', '#999999', '#CCCCCC'],
    description: 'Classic black to white gradient'
  },
  tropical: {
    name: 'Tropical',
    colors: ['#FF6B9D', '#C44569', '#F8B500', '#6BCF7F', '#4BCFFA'],
    description: 'Vibrant tropical colors'
  }
};

export const generateRandomPalette = (): ColorPalette => {
  const colors = [];
  for (let i = 0; i < 8; i++) {
    const color = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
    colors.push(color.toUpperCase());
  }

  return {
    name: 'Random',
    colors,
    description: 'A randomly generated color palette'
  };
};

// Color format conversion utilities
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 'rgb(0, 0, 0)';
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgb(${r}, ${g}, ${b})`;
};

const hexToHsl = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 'hsl(0, 0%, 0%)';

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
};

export const getMockPalette = (name: string): ColorPalette => {
  const lowerName = name.toLowerCase();
  return mockColorPalettes[lowerName] || generateRandomPalette();
};

export interface GeneratedPalette {
  colors: string[];
  inspiration: string;
}

// Helper to invoke runAgent edge function
const invokeRunAgent = async (agent: string, messages: { role: string; content: string }[]): Promise<string> => {
  const { supabase } = await import('@/integrations/supabase/client');
  const { data, error } = await supabase.functions.invoke('runAgent', {
    body: {
      agent,
      messages,
      temperature: 0.7,
      stream: false
    }
  });

  if (error) {
    throw new Error(error.message || 'Failed to fetch from runAgent');
  }

  return data.response || '';
};

export const generateColorPaletteWithAI = async (
  inputValue: string,
  _isDarkMode?: boolean
): Promise<GeneratedPalette> => {
  try {
    const query = inputValue.trim() || "generate a random color palette";

    const response = await invokeRunAgent('color-palette', [
      { role: 'user', content: query }
    ]);

    // Extract hex codes from response
    const hexPattern = /#[0-9A-Fa-f]{6}/g;
    const colors = response.match(hexPattern) || [];

    // Extract inspiration from the first line (before any hex codes)
    let inspiration = "Creative Harmony";
    const lines = response.trim().split('\n');

    // Find the first non-empty line that doesn't contain a hex code
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine && !/#[0-9A-Fa-f]{6}/i.test(trimmedLine)) {
        // Clean up the title - remove any quotes, asterisks, or other markdown
        const cleanTitle = trimmedLine
          .replace(/^["'\*#]+/, '')
          .replace(/["'\*#]+$/, '')
          .trim();
        if (cleanTitle.length > 0 && cleanTitle.length < 50) {
          inspiration = cleanTitle;
          break;
        }
      }
    }

    if (colors.length === 0) {
      // Use mock palette as fallback
      const mockPalette = generateRandomPalette();
      return { colors: mockPalette.colors, inspiration };
    } else {
      return { colors: colors.slice(0, 9), inspiration };
    }
  } catch (error) {
    console.error('Color palette AI error:', error);
    // Use mock palette as fallback on error
    const mockPalette = generateRandomPalette();
    return { colors: mockPalette.colors, inspiration: 'Creative Harmony' };
  }
};
// NOTE: UI rendering is now handled by React components.

const generateColorPaletteHTML = (colors: string[], isDarkMode?: boolean): string => {
  const colorItems = colors.map(color => `
    <div class="color-item">
      <div class="w-16 h-16 rounded-lg cursor-pointer transition-transform hover:scale-105 border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}" 
           style="background-color: ${color}" 
           onclick="navigator.clipboard.writeText('${color}').then(() => {
             // Show toast notification
             const toast = document.createElement('div');
             toast.textContent = 'Color ${color} copied!';
             toast.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #10b981; color: white; padding: 8px 16px; border-radius: 8px; z-index: 10000; font-size: 14px;';
             document.body.appendChild(toast);
             setTimeout(() => document.body.removeChild(toast), 2000);
           })"></div>
      <p class="text-xs mt-1 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}">${color}</p>
    </div>
  `).join('');

  return `
    <div class="color-palette-container p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}">
      <h3 class="text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}">🎨 AI Generated Color Palette</h3>
      <div class="grid gap-2 mb-4" style="grid-template-columns: repeat(${Math.min(colors.length, 8)}, 1fr);">
        ${colorItems}
      </div>
      <p class="text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}">Click any color to copy to clipboard</p>
    </div>`;
};
